import { Vector3, AbstractMesh, Scene, GPUParticleSystem, Texture } from '@babylonjs/core';
import { IPhysicsEngine, IPhysicsBody, PhysicsShapeType, PhysicsBodyConfig, CollisionGroups, CollisionMasks } from './IPhysicsEngine';
import { AmmoPhysicsEngine } from './AmmoPhysicsEngine';
import { logger } from '../../core/Logger';
import { StateManager, ActionHandlerRegistry } from '../../state';
import { ServiceRegistry } from '../../core/ServiceRegistry';

/**
 * Physics engine types
 */
export enum PhysicsEngineType {
  AMMO = 'ammo',
  HAVOK = 'havok'
}

/**
 * Performance monitoring stats
 */
export interface PhysicsPerformanceStats {
  activeBodies: number;
  staticBodies: number;
  dynamicBodies: number;
  kinematicBodies: number;
  collisionChecks: number;
  frameTime: number;
}

/**
 * PhysicsManager - Central physics management system
 * Provides a clean API for physics operations and manages engine lifecycle
 */
export class PhysicsManager {
  private engine: IPhysicsEngine | null = null;
  private bodies: Map<string, IPhysicsBody> = new Map();
  /** 
   * Bodies that use manual gravity (characters).
   * These bodies have physics engine gravity disabled and handle gravity in their update loop.
   * Gravity zones affect these through ServiceRegistry.getEntityGravityOverride().
   */
  private manualGravityBodies: Set<string> = new Set();
  private engineType: PhysicsEngineType = PhysicsEngineType.AMMO;
  private lastCollisionPairs: Set<string> = new Set();
  private _dispatcherWarningLogged: boolean = false;
  private performanceStats: PhysicsPerformanceStats = {
    activeBodies: 0,
    staticBodies: 0,
    dynamicBodies: 0,
    kinematicBodies: 0,
    collisionChecks: 0,
    frameTime: 0
  };

  constructor(private scene: Scene) {}

  /**
   * Create a BabylonJS GPUParticleSystem.
   * Sprint 0024 Step 1.5: centralize GPU particle creation under PhysicsManager.
   */
  createGpuParticleSystem(
    id: string,
    capacity: number = 1000,
    options?: {
      randomTextureSize?: number;
      texturePath?: string;
    }
  ): GPUParticleSystem {
    const system = new GPUParticleSystem(
      id,
      {
        capacity,
        randomTextureSize: options?.randomTextureSize ?? 256,
      },
      this.scene
    );

    if (options?.texturePath) {
      system.particleTexture = new Texture(options.texturePath, this.scene);
    }

    return system;
  }

  /**
   * Check if physics is initialized
   */
  isInitialized(): boolean {
    return this.engine !== null;
  }

  /**
   * Initialize physics engine
   */
  async initialize(
    gravity: Vector3 = new Vector3(0, -30, 0),
    engineType: PhysicsEngineType = PhysicsEngineType.AMMO
  ): Promise<void> {
    // Skip if already initialized
    if (this.engine) {
      logger.physics.debug('Physics already initialized, skipping');
      return;
    }
    
    this.engineType = engineType;

    // Create appropriate engine
    switch (engineType) {
      case PhysicsEngineType.AMMO:
        this.engine = new AmmoPhysicsEngine(this.scene);
        break;
      case PhysicsEngineType.HAVOK:
        throw new Error('Havok physics not yet implemented');
      default:
        throw new Error(`Unknown physics engine type: ${engineType}`);
    }

    // Initialize the engine
    await this.engine.initialize(gravity);
    
    logger.physics.log(`✅ PhysicsManager initialized with ${engineType} engine`);
  }

  /**
   * Create a physics body for a mesh
   */
  createBody(
    id: string,
    mesh: AbstractMesh,
    shapeType: PhysicsShapeType,
    config: PhysicsBodyConfig
  ): IPhysicsBody {
    if (!this.engine) {
      throw new Error('Physics engine not initialized. Call initialize() first.');
    }

    // Remove existing body if it exists
    if (this.bodies.has(id)) {
      logger.physics.warn(`Body with id "${id}" already exists, removing old one`);
      this.removeBody(id);
    }

    logger.physics.log(`[CREATE BODY] Creating physics body with ID: ${id} for mesh: ${mesh.name}`);
    
    // Create body
    const body = this.engine.createBody(mesh, shapeType, config);
    this.bodies.set(id, body);
    
    logger.physics.log(`[CREATE BODY] Body created and stored in map with ID: ${id}`);

    return body;
  }

  /**
   * Get a physics body by ID
   */
  getBody(id: string): IPhysicsBody | undefined {
    return this.bodies.get(id);
  }

  /**
   * Get all physics bodies with their IDs
   */
  getAllBodies(): Map<string, IPhysicsBody> {
    return this.bodies;
  }

  /**
   * Remove a physics body
   */
  removeBody(id: string): void {
    const body = this.bodies.get(id);
    if (body && this.engine) {
      this.engine.removeBody(body);
      this.bodies.delete(id);
      this.manualGravityBodies.delete(id);
    }
  }

  /**
   * Update collision filtering for an existing body.
   */
  setCollisionFilter(bodyId: string, collisionGroup?: number, collisionMask?: number): void {
    const body = this.bodies.get(bodyId);
    if (!body || !this.engine) return;
    this.engine.setCollisionFilter(body, collisionGroup, collisionMask);
  }

  /**
   * Set gravity
   */
  setGravity(gravity: Vector3): void {
    if (!this.engine) {
      throw new Error('Physics engine not initialized');
    }
    this.engine.setGravity(gravity);
  }

  /**
   * Get current gravity
   */
  getGravity(): Vector3 {
    if (!this.engine) {
      throw new Error('Physics engine not initialized');
    }
    return this.engine.getGravity();
  }

  /**
   * Apply per-body gravity overrides from trigger zones.
   * Called each frame before physics step.
   * Applies custom gravity to bodies that are inside gravity zones,
   * and resets bodies that have exited to world gravity.
   * 
   * Note: Bodies with manual gravity (characters) are skipped here.
   * They handle gravity overrides in their own updatePhysics() via ServiceRegistry.
   */
  private applyGravityOverrides(): void {
    if (!ServiceRegistry.isInitialized()) return;
    
    const worldGravity = this.engine?.getGravity() ?? new Vector3(0, -30, 0);
    
    for (const [bodyId, body] of this.bodies) {
      // Skip bodies that handle gravity manually (e.g., characters)
      if (this.manualGravityBodies.has(bodyId)) continue;
      
      const override = ServiceRegistry.getEntityGravityOverride(bodyId);
      
      if (override) {
        // Entity is inside a gravity zone - apply custom gravity
        body.setBodyGravity(override);
      } else {
        // Entity is not in any gravity zone - use world gravity
        body.setBodyGravity(worldGravity);
      }
    }
  }

  /**
   * Update physics simulation
   */
  update(deltaTime: number): void {
    if (this.engine) {
      const startTime = performance.now();
      
      // Apply per-body gravity overrides from trigger zones
      this.applyGravityOverrides();
      
      this.engine.update(deltaTime);

      // Global physics step hook (Sprint 0024)
      ActionHandlerRegistry.onPhysicsStep(deltaTime);

      // Collision pair tracking (Ammo only for now)
      if (this.engineType === PhysicsEngineType.AMMO) {
        this.pollAmmoCollisions();
      }

      this.performanceStats.frameTime = performance.now() - startTime;
      this.performanceStats.activeBodies = this.bodies.size;
    }
  }

  private pollAmmoCollisions(): void {
    if (!this.engine) {
      // logger.physics.debug('[COLLISION POLL] No engine');
      return;
    }

    const plugin = this.engine.getPlugin() as any;
    const dispatcher = plugin?._dispatcher ?? plugin?.world?.getDispatcher?.();
    if (!dispatcher || typeof dispatcher.getNumManifolds !== 'function') {
      // Log once every 60 frames to avoid spam
      if (!this._dispatcherWarningLogged) {
        logger.physics.warn('[COLLISION POLL] No dispatcher found or missing getNumManifolds');
        this._dispatcherWarningLogged = true;
      }
      return;
    }

    const numManifolds: number = dispatcher.getNumManifolds();
    this.performanceStats.collisionChecks = numManifolds;
    
    // Debug: log manifolds periodically (once per second roughly)
    if (numManifolds > 0 && Math.random() < 0.016) {
      logger.physics.debug(`[COLLISION POLL] Found ${numManifolds} manifolds`);
    }

    const currentPairs: Set<string> = new Set();
    
    // Debug stats (log once per second roughly)
    const shouldLogDebug = Math.random() < 0.016;
    let debugStats = { total: numManifolds, noContacts: 0, noHasContact: 0, noPtr: 0, added: 0 };

    for (let i = 0; i < numManifolds; i++) {
      const manifold = dispatcher.getManifoldByIndexInternal(i);
      if (!manifold || typeof manifold.getNumContacts !== 'function') continue;

      const numContacts: number = manifold.getNumContacts();
      if (numContacts <= 0) {
        debugStats.noContacts++;
        continue;
      }

      // Consider this pair "colliding" if any contact has penetration (distance <= 0).
      let hasContact = false;
      for (let c = 0; c < numContacts; c++) {
        const contactPoint = manifold.getContactPoint(c);
        if (!contactPoint || typeof contactPoint.getDistance !== 'function') continue;
        if (contactPoint.getDistance() <= 0) {
          hasContact = true;
          break;
        }
      }
      if (!hasContact) {
        debugStats.noHasContact++;
        continue;
      }

      const body0 = manifold.getBody0?.();
      const body1 = manifold.getBody1?.();
      
      // Ammo.js Emscripten bindings: use Ammo.getPointer() to get native pointer
      // The .ptr property may not exist on wrapper objects returned from getBody0/getBody1
      let ptr0: number | undefined = body0?.ptr;
      let ptr1: number | undefined = body1?.ptr;
      
      // Try Ammo.getPointer() if .ptr is not available (Emscripten binding method)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const AmmoAny = Ammo as any;
      if (ptr0 === undefined && body0 && typeof AmmoAny?.getPointer === 'function') {
        try { ptr0 = AmmoAny.getPointer(body0); } catch { /* ignore */ }
      }
      if (ptr1 === undefined && body1 && typeof AmmoAny?.getPointer === 'function') {
        try { ptr1 = AmmoAny.getPointer(body1); } catch { /* ignore */ }
      }
      
      if (!ptr0 || !ptr1) {
        debugStats.noPtr++;
        if (shouldLogDebug) {
          const hasAmmoGetPointer = typeof AmmoAny?.getPointer === 'function';
          logger.physics.debug(`[COLLISION POLL] NoPtr: body0=${!!body0}, body1=${!!body1}, ptr0=${ptr0}, ptr1=${ptr1}, Ammo.getPointer=${hasAmmoGetPointer}`);
        }
        continue;
      }

      const key = ptr0 < ptr1 ? `${ptr0}|${ptr1}` : `${ptr1}|${ptr0}`;
      currentPairs.add(key);
      debugStats.added++;
    }
    
    if (shouldLogDebug) {
      logger.physics.debug(`[COLLISION POLL] Stats: manifolds=${debugStats.total}, noContacts=${debugStats.noContacts}, noHasContact=${debugStats.noHasContact}, noPtr=${debugStats.noPtr}, validPairs=${debugStats.added}, lastPairs=${this.lastCollisionPairs.size}`);
    }

    // Emit start events
    for (const key of currentPairs) {
      if (!this.lastCollisionPairs.has(key)) {
        this.emitCollisionEvent('start', key);
      }
    }

    // Emit end events
    for (const key of this.lastCollisionPairs) {
      if (!currentPairs.has(key)) {
        this.emitCollisionEvent('end', key);
      }
    }

    this.lastCollisionPairs = currentPairs;
  }

  private emitCollisionEvent(kind: 'start' | 'end', pairKey: string): void {
    const [aPtrStr, bPtrStr] = pairKey.split('|');
    const aPtr = Number(aPtrStr);
    const bPtr = Number(bPtrStr);
    if (!Number.isFinite(aPtr) || !Number.isFinite(bPtr)) return;

    const bodyAId = this.resolveBodyIdFromAmmoPtr(aPtr);
    const bodyBId = this.resolveBodyIdFromAmmoPtr(bPtr);

    // Global hooks
    if (kind === 'start') {
      ActionHandlerRegistry.onGlobalCollisionStart(bodyAId, bodyBId, pairKey);
    } else {
      ActionHandlerRegistry.onGlobalCollisionEnd(bodyAId, bodyBId, pairKey);
    }

    // Character-specific XState + handler hooks
    this.emitCharacterCollisionIfRelevant(kind, bodyAId, bodyBId, pairKey);
    this.emitCharacterCollisionIfRelevant(kind, bodyBId, bodyAId, pairKey);
  }

  private emitCharacterCollisionIfRelevant(
    kind: 'start' | 'end',
    selfBodyId?: string,
    otherBodyId?: string,
    pairKey?: string
  ): void {
    if (!selfBodyId) return;
    const prefix = 'character_physics_';
    if (!selfBodyId.startsWith(prefix)) return;

    const characterId = selfBodyId.slice(prefix.length);
    const stateManager = StateManager.getInstance();

    if (kind === 'start') {
      stateManager.sendCharacterEvent(characterId, {
        type: 'PHYSICS_COLLISION_START',
        otherBodyId,
        pairKey,
      });
      ActionHandlerRegistry.onCollisionStart(characterId, otherBodyId, pairKey);
    } else {
      stateManager.sendCharacterEvent(characterId, {
        type: 'PHYSICS_COLLISION_END',
        otherBodyId,
        pairKey,
      });
      ActionHandlerRegistry.onCollisionEnd(characterId, otherBodyId, pairKey);
    }
  }

  private resolveBodyIdFromAmmoPtr(ptr: number): string | undefined {
    // Lazily scan existing bodies and match by btRigidBody ptr.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const AmmoAny = Ammo as any;
    for (const [id, body] of this.bodies.entries()) {
      const impostor = (body as any)?.impostor;
      const physicsBody = impostor?.physicsBody;
      
      // Try .ptr first, then Ammo.getPointer()
      let physicsBodyPtr: number | undefined = physicsBody?.ptr;
      if (physicsBodyPtr === undefined && physicsBody && typeof AmmoAny?.getPointer === 'function') {
        try { physicsBodyPtr = AmmoAny.getPointer(physicsBody); } catch { /* ignore */ }
      }
      
      if (physicsBodyPtr === ptr) return id;
    }
    return undefined;
  }

  /**
   * Check if a physics body has ground contact (contact normal pointing mostly upward).
   * This is used for physics-driven ground detection.
   * 
   * @param bodyId The ID of the physics body to check
   * @param minNormalY Minimum Y component of contact normal to consider as "ground" (default: 0.5 = ~60° slope)
   * @returns true if the body has at least one ground contact
   */
  hasGroundContact(bodyId: string, minNormalY: number = 0.5): boolean {
    if (!this.engine || this.engineType !== PhysicsEngineType.AMMO) {
      return false;
    }

    const body = this.bodies.get(bodyId);
    if (!body) {
      return false;
    }

    const impostor = (body as any)?.impostor;
    const physicsBody = impostor?.physicsBody;
    
    // Try .ptr first, then Ammo.getPointer()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const AmmoAny = Ammo as any;
    let bodyPtr: number | undefined = physicsBody?.ptr;
    if (bodyPtr === undefined && physicsBody && typeof AmmoAny?.getPointer === 'function') {
      try { bodyPtr = AmmoAny.getPointer(physicsBody); } catch { /* ignore */ }
    }
    if (!bodyPtr) return false;

    const plugin = this.engine.getPlugin() as any;
    const dispatcher = plugin?._dispatcher ?? plugin?.world?.getDispatcher?.();
    if (!dispatcher || typeof dispatcher.getNumManifolds !== 'function') return false;

    const numManifolds: number = dispatcher.getNumManifolds();

    for (let i = 0; i < numManifolds; i++) {
      const manifold = dispatcher.getManifoldByIndexInternal(i);
      if (!manifold || typeof manifold.getNumContacts !== 'function') continue;

      const body0 = manifold.getBody0?.();
      const body1 = manifold.getBody1?.();
      
      // Try .ptr first, then Ammo.getPointer()
      let ptr0: number | undefined = body0?.ptr;
      let ptr1: number | undefined = body1?.ptr;
      if (ptr0 === undefined && body0 && typeof (AmmoAny as any)?.getPointer === 'function') {
        try { ptr0 = (AmmoAny as any).getPointer(body0); } catch { /* ignore */ }
      }
      if (ptr1 === undefined && body1 && typeof (AmmoAny as any)?.getPointer === 'function') {
        try { ptr1 = (AmmoAny as any).getPointer(body1); } catch { /* ignore */ }
      }

      // Check if this manifold involves our body
      const isBody0 = ptr0 === bodyPtr;
      const isBody1 = ptr1 === bodyPtr;
      if (!isBody0 && !isBody1) continue;

      const numContacts: number = manifold.getNumContacts();
      for (let c = 0; c < numContacts; c++) {
        const contactPoint = manifold.getContactPoint(c);
        if (!contactPoint || typeof contactPoint.getDistance !== 'function') continue;

        // Only consider contacts that are actually touching (penetration <= 0)
        if (contactPoint.getDistance() > 0.01) continue;

        // Get contact normal (points from body0 to body1)
        const normalOnB = contactPoint.get_m_normalWorldOnB?.();
        if (!normalOnB) continue;

        const normalY = normalOnB.y();

        // If our body is body0, the normal points away from us
        // If our body is body1, the normal points toward us
        // We want the normal pointing UP from the ground INTO our body
        const groundNormalY = isBody0 ? -normalY : normalY;

        // If the normal points mostly upward (ground under us), we have ground contact
        if (groundNormalY >= minNormalY) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Perform a physics raycast from start to end position.
   * @param from Starting position of the ray
   * @param to Ending position of the ray
   * @param collisionMask Optional collision mask to filter what the ray can hit
   * @returns Hit information or null if no hit
   */
  raycast(
    from: Vector3,
    to: Vector3,
    collisionMask?: number
  ): { hitPoint: Vector3; hitNormal: Vector3; distance: number } | null {
    if (!this.engine?.raycast) {
      return null;
    }
    const result = this.engine.raycast(from, to, collisionMask);
    // Debug: Log raycast result occasionally
    if (Math.random() < 0.02) {
      logger.physics.debug(`[Raycast] from=(${from.x.toFixed(2)}, ${from.y.toFixed(2)}, ${from.z.toFixed(2)}) to=(${to.x.toFixed(2)}, ${to.y.toFixed(2)}, ${to.z.toFixed(2)}) mask=${collisionMask} => ${result ? `HIT at (${result.hitPoint.x.toFixed(2)}, ${result.hitPoint.y.toFixed(2)}, ${result.hitPoint.z.toFixed(2)}) normal=(${result.hitNormal.x.toFixed(2)}, ${result.hitNormal.y.toFixed(2)}, ${result.hitNormal.z.toFixed(2)})` : 'NO HIT'}`);
    }
    return result;
  }

  /**
   * Get performance statistics
   */
  getPerformanceStats(): Readonly<PhysicsPerformanceStats> {
    return this.performanceStats;
  }

  /**
   * Check if physics is ready
   */
  isReady(): boolean {
    return this.engine?.isReady() ?? false;
  }

  /**
   * Get the underlying physics engine (for advanced use)
   */
  getEngine(): IPhysicsEngine | null {
    return this.engine;
  }

  /**
   * Get current engine type
   */
  getEngineType(): PhysicsEngineType {
    return this.engineType;
  }

  /**
   * Set the physics simulation time step.
   * Call this each frame with scaled delta time for time scaling effects (slow-mo, etc.)
   * @param timeStep Time step in seconds
   */
  setTimeStep(timeStep: number): void {
    if (this.engineType === PhysicsEngineType.AMMO && this.engine) {
      (this.engine as AmmoPhysicsEngine).setTimeStep(timeStep);
    }
  }

  /**
   * Dispose physics system
   */
  dispose(): void {
    // Remove all bodies
    for (const id of Array.from(this.bodies.keys())) {
      this.removeBody(id);
    }
    
    // Dispose engine
    if (this.engine) {
      this.engine.dispose();
      this.engine = null;
    }
    
    logger.physics.debug('PhysicsManager disposed');
  }

  /**
   * Helper: Create a static box collider
   */
  createStaticBox(
    id: string,
    mesh: AbstractMesh,
    friction: number = 1.0
  ): IPhysicsBody {
    return this.createBody(id, mesh, PhysicsShapeType.BOX, {
      mass: 0,
      friction,
      restitution: 0
    });
  }

  /**
   * Helper: Create a dynamic capsule (for characters)   * Uses ANIMATED motion type to prevent tipping
   */
  createKinematicCapsule(
    id: string,
    mesh: AbstractMesh,
    friction: number = 0.5
  ): IPhysicsBody {
    return this.createBody(id, mesh, PhysicsShapeType.CAPSULE, {
      mass: 0, // Kinematic bodies have mass 0
      friction,
      restitution: 0.0,
      isKinematic: true  // Mark as kinematic
    });
  }

  /**
   * Create a dynamic capsule for physics objects   */
  createDynamicCapsule(
    id: string,
    mesh: AbstractMesh,
    mass: number = 1.0,
    friction: number = 0.5,
    restitution: number = 0.0
  ): IPhysicsBody {
    return this.createBody(id, mesh, PhysicsShapeType.CAPSULE, {
      mass,
      friction,
      restitution
    });
  }

  /**
   * Helper: Create a static mesh collider using triangle mesh for proper collision.
   * This uses manual Ammo.js triangle mesh creation which works reliably for imported GLB meshes.
   * Falls back to BOX shape if the mesh has no vertex data.
   */
  createStaticMesh(
    id: string,
    mesh: AbstractMesh,
    friction: number = 1.0
  ): IPhysicsBody {
    const engine = this.engine as any; // Access AmmoPhysicsEngine directly
    
    // Try to use the triangle mesh method if available
    if (typeof engine.createStaticTriangleMesh === 'function') {
      const body = engine.createStaticTriangleMesh(mesh, {
        mass: 0,
        friction,
        restitution: 0,
        collisionGroup: CollisionGroups.STATIC,
        collisionMask: CollisionMasks.STATIC
      });
      
      if (body) {
        this.bodies.set(id, body);
        logger.physics.log(`Created static triangle mesh for: ${id}`);
        return body;
      }
      
      // Triangle mesh creation failed, fall back to box
      logger.physics.warn(`Triangle mesh failed for ${id}, falling back to box collider`);
    }
    
    // Fallback: use box shape
    return this.createBody(id, mesh, PhysicsShapeType.BOX, {
      mass: 0,
      friction,
      restitution: 0,
      collisionGroup: CollisionGroups.STATIC,
      collisionMask: CollisionMasks.STATIC
    });
  }

  /**
   * Helper: Create a static box with STATIC collision group
   */
  createStaticBoxWithGroup(
    id: string,
    mesh: AbstractMesh,
    friction: number = 1.0
  ): IPhysicsBody {
    return this.createBody(id, mesh, PhysicsShapeType.BOX, {
      mass: 0,
      friction,
      restitution: 0,
      collisionGroup: CollisionGroups.STATIC,
      collisionMask: CollisionMasks.STATIC
    });
  }

  /**
   * Helper: Create a dynamic box with DYNAMIC collision group (phases through characters)
   */
  createDynamicBox(
    id: string,
    mesh: AbstractMesh,
    mass: number = 10.0,
    friction: number = 0.6,
    restitution: number = 0.2
  ): IPhysicsBody {
    return this.createBody(id, mesh, PhysicsShapeType.BOX, {
      mass,
      friction,
      restitution,
      collisionGroup: CollisionGroups.DYNAMIC,
      collisionMask: CollisionMasks.DYNAMIC
    });
  }

  /**
   * Helper: Create a character capsule with CHARACTER collision group (phases through dynamic objects)
   */
  createCharacterCapsule(
    id: string,
    mesh: AbstractMesh,
    mass: number = 70.0,
    friction: number = 0.5,
    collisionMask: number = CollisionMasks.CHARACTER,
    collisionGroup: number = CollisionGroups.CHARACTER
  ): IPhysicsBody {
    const body = this.createBody(id, mesh, PhysicsShapeType.CAPSULE, {
      mass,
      friction,
      restitution: 0.0,
      collisionGroup,
      collisionMask
    });
    
    // Mark this body as using manual gravity.
    // Characters handle gravity in their updatePhysics() loop, not via physics engine.
    // Gravity zones affect characters via ServiceRegistry.getEntityGravityOverride().
    this.manualGravityBodies.add(id);
    
    return body;
  }

  // ============================================
  // SPAWN POINT MANAGEMENT
  // ============================================
  
  private spawnPoint: Vector3 | null = null;
  
  /**
   * Set the level spawn point (called by LevelBuilder)
   */
  setSpawnPoint(point: Vector3): void {
    this.spawnPoint = point.clone();
    logger.physics.log(`📍 Spawn point set: ${point.toString()}`);
  }
  
  /**
   * Get the current spawn point
   */
  getSpawnPoint(): Vector3 | null {
    return this.spawnPoint;
  }
  
  /**
   * Teleport a physics body to a new position.
   * Resets velocity and properly updates physics state.
   */
  teleportBody(bodyId: string, position: Vector3): boolean {
    const body = this.bodies.get(bodyId);
    if (!body) {
      logger.physics.warn(`Cannot teleport: body '${bodyId}' not found`);
      return false;
    }
    
    // Reset velocity before teleporting
    body.setLinearVelocity(Vector3.Zero());
    body.setAngularVelocity(Vector3.Zero());
    
    // Set new position
    body.setPosition(position);
    
    logger.physics.log(`📍 Teleported body '${bodyId}' to ${position.toString()}`);
    return true;
  }

  /**
   * Debug: Get collision objects from Ammo physics world
   */
  getCollisionObjectsDebugInfo(): Array<{
    position: Vector3;
    shape: string;
    isStatic: boolean;
  }> {
    const engine = this.engine as any;
    if (typeof engine.getCollisionObjectsDebugInfo === 'function') {
      return engine.getCollisionObjectsDebugInfo();
    }
    return [];
  }
}
